import { type FC } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';

import { useCurrencyStore } from '../../hooks/currencyStore/currencyStore.ts';
import { useHeaderFooterServerVars } from '../HeaderFooterServerVarsContext/HeaderFooterServerVarsContext.tsx';
import { type CartAddedNotificationPayload } from '../../actions/cartActions.ts';
import { CartAddedNotificationContent } from './CartAddedNotificationContent.tsx';
import { type CartNotificationQuery as CartNotificationQueryType } from './__generated__/CartNotificationQuery.graphql.ts';

const cartNotificationQuery = graphql`
    query CartNotificationQuery(
        $cartItemId: Int
        $fetchCartItem: Boolean!
        $itemIds: [String]
        $fetchItem: Boolean!
        $userCountryCode: String
    ) {
        viewer {
            cartItem(cartItemId: $cartItemId) @include(if: $fetchCartItem) {
                skuId
                item {
                    title
                    photos {
                        versions {
                            webPath
                            type {
                                name
                            }
                        }
                    }
                    skus {
                        serviceId
                        ImageMultiSkuOverlayImageSrc: firstPhotoWebPath(size: small)
                    }
                    pricing(userCountryCode: $userCountryCode) {
                        netPrice {
                            convertedAmountList {
                                currency
                                amount
                            }
                        }
                        salePrice {
                            convertedAmountList {
                                currency
                                amount
                            }
                        }
                        retailPrice {
                            convertedAmountList {
                                currency
                                amount
                            }
                        }
                    }
                }
                sku {
                    firstPhotoWebPath(size: small)
                }
            }
            items(itemIds: $itemIds, first: 1) @include(if: $fetchItem) {
                edges {
                    node {
                        title
                        photos {
                            versions {
                                webPath
                                type {
                                    name
                                }
                            }
                        }
                        skus {
                            serviceId
                            ImageMultiSkuOverlayImageSrc: firstPhotoWebPath(size: small)
                        }
                        pricing(userCountryCode: $userCountryCode) {
                            netPrice {
                                convertedAmountList {
                                    currency
                                    amount
                                }
                            }
                            salePrice {
                                convertedAmountList {
                                    currency
                                    amount
                                }
                            }
                            retailPrice {
                                convertedAmountList {
                                    currency
                                    amount
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

type ViewerData = CartNotificationQueryType['response']['viewer'];
type NotificationItem = NonNullable<NonNullable<ViewerData['cartItem']>['item']>;

function getItemPrice({
    item,
    currency,
    isUserTypeTrade,
}: {
    item: NotificationItem | null | undefined;
    currency: string;
    isUserTypeTrade: boolean;
}): number | null {
    if (!item) {
        return null;
    }
    const netPrices = item.pricing?.netPrice?.convertedAmountList;
    const salePrices = item.pricing?.salePrice?.convertedAmountList;
    let prices = item.pricing?.retailPrice?.convertedAmountList;
    if (salePrices) {
        prices = salePrices;
    } else if (isUserTypeTrade && netPrices) {
        prices = netPrices;
    }
    const priceObj = (prices || []).filter(p => p?.currency === currency);
    return priceObj?.[0]?.amount ?? null;
}

function getItemImagePath({
    item,
    skuId,
    skuImagePath,
}: {
    item: NotificationItem | null | undefined;
    skuId: string | null;
    skuImagePath: string | null;
}): string | null {
    if (skuImagePath) {
        return skuImagePath;
    }
    const photoObj = (item?.photos?.[0]?.versions || []).filter(version => {
        const type = version?.type;
        return type && type.name === 'small';
    });
    const sku = item?.skus?.find(itemSku => itemSku?.serviceId === skuId);
    return sku?.ImageMultiSkuOverlayImageSrc || photoObj?.[0]?.webPath || null;
}

export const CartAddedNotificationLoader: FC<{
    notification: CartAddedNotificationPayload;
    onClose: () => void;
    isMobile: boolean;
}> = ({ notification, onClose, isMobile }) => {
    const { cartItemId, itemId, skuId } = notification;
    const fetchCartItem = !!cartItemId;
    const fetchItem = !fetchCartItem && !!(itemId || skuId);
    const { currency } = useCurrencyStore();
    const { isUserTypeTrade } = useHeaderFooterServerVars();

    const data = useLazyLoadQuery<CartNotificationQueryType>(
        cartNotificationQuery,
        {
            cartItemId: fetchCartItem ? cartItemId : null,
            fetchCartItem,
            itemIds: fetchItem && itemId ? [itemId] : [],
            fetchItem,
            userCountryCode: getUserSessionCountryCode(),
        },
        { fetchPolicy: 'store-or-network' }
    );

    const cartItem = data.viewer.cartItem;
    const localItem = data.viewer.items?.edges?.[0]?.node;
    const item = cartItem?.item || localItem;
    const resolvedSkuId = cartItem?.skuId || skuId;
    const skuImagePath = cartItem?.sku?.firstPhotoWebPath || null;

    return (
        <CartAddedNotificationContent
            title={item?.title || null}
            imagePath={getItemImagePath({ item, skuId: resolvedSkuId, skuImagePath })}
            price={getItemPrice({ item, currency, isUserTypeTrade: !!isUserTypeTrade })}
            currency={currency}
            onClose={onClose}
            isMobile={isMobile}
        />
    );
};
